import { useState, useCallback, useEffect } from 'react';
import { formatAmount as formatCurrency, parseAmount as parseCurrencyInput } from '@/utils/currency';
import type { CurrencyCode } from '@/shared/types/currency.types';

interface InputAmountProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  currency?: CurrencyCode;
  min?: number;
  max?: number;
  error?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
  showConversion?: boolean;
  conversionCurrency?: CurrencyCode;
  conversionRate?: number;
}

export function InputAmount({
  label,
  value,
  onChange,
  currency = 'GNF',
  min = 0,
  max,
  error,
  hint,
  disabled = false,
  className = '',
  showConversion = false,
  conversionCurrency = 'XOF',
  conversionRate = 1,
}: InputAmountProps) {
  const [displayValue, setDisplayValue] = useState(value.toString());
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toLocaleString('fr-FR'));
    }
  }, [value, isFocused]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, '');
    const numericValue = parseInt(rawValue, 10) || 0;

    if (max && numericValue > max) {
      return;
    }

    setDisplayValue(rawValue ? numericValue.toLocaleString('fr-FR') : '');
    onChange(numericValue);
  }, [max, onChange]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (value > 0) {
      setDisplayValue(value.toLocaleString('fr-FR'));
    }
  }, [value]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setDisplayValue(value.toString() || '');
  }, [value]);

  const conversionAmount = value * conversionRate;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
          className={`
            block w-full rounded-lg border bg-white px-3 py-2.5 pr-16 text-gray-900 text-right
            placeholder:text-gray-400
            focus:outline-none focus:ring-2 focus:ring-offset-0
            transition-colors duration-200
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
            }
          `}
          min={min}
          max={max}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">
          {currency}
        </span>
      </div>
      {showConversion && value > 0 && (
        <p className="mt-1 text-sm text-gray-500">
          env. {formatCurrency(conversionAmount, conversionCurrency)}
        </p>
      )}
      {error && (
        <p className="mt-1.5 text-sm text-red-600">{error}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-500">{hint}</p>
      )}
    </div>
  );
}
